import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Tratar erros de rede ou stream
    if (!error.response) {
      error.response = {
        data: {
          detail: 'Erro de conexão. Verifique sua internet.'
        }
      };
      return Promise.reject(error);
    }
    
    // Only redirect to login on 401 if NOT already on login page
    // and NOT on the login endpoint
    if (error.response?.status === 401 && 
        !error.config.url.includes('/api/auth/login') &&
        window.location.pathname !== '/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/api/auth/login', { email, password });
export const getMe = () => api.get('/api/auth/me');

// Admins (Super Admin)
export const getAdmins = () => api.get('/api/admins');
export const getAdmin = (id) => api.get(`/api/admins/${id}`);
export const getAdminDossier = (id) => api.get(`/api/admins/${id}/dossier`);
// Monitoramento de acesso: liga/desliga o aviso por e-mail quando a conta acessa.
export const setAdminMonitor = (id, active) => api.post(`/api/admins/${id}/monitor`, { active });
export const createAdmin = (data) => api.post('/api/admins', data);
export const updateAdmin = (id, data) => api.patch(`/api/admins/${id}`, data);
export const deleteAdmin = (id) => api.delete(`/api/admins/${id}`);
export const impersonateAdmin = (adminId) => api.post(`/api/super/impersonate/${adminId}`);

// Contas Bloqueadas (Super Admin)
export const getBlockedAccounts = () => api.get('/api/super/blocked-accounts');

// Servidores Evolution (Super Admin) — coleção wasender_pats no backend
export const getPatRepository = () => api.get('/api/super/pat-repository');
export const createPat = (data) => api.post('/api/super/pat-repository', data);
export const updatePat = (id, data) => api.patch(`/api/super/pat-repository/${id}`, data);
export const deletePat = (id) => api.delete(`/api/super/pat-repository/${id}`);
export const testServerConnection = (data) => api.post('/api/super/pat-repository/test-connection', data);
export const getSystemInfo = () => api.get('/api/super/system-info');
export const migratePatTenants = (id, data) => api.post(`/api/super/pat-repository/${id}/migrate`, data);

// Super Admin Overview
export const getSuperOverview = () => api.get('/api/super/overview');
export const getSuperMessages = (params) => api.get('/api/super/messages', { params });
export const getSuperPresence = () => api.get('/api/super/presence');
export const getSuperScheduledCampaigns = () => api.get('/api/super/scheduled-campaigns');
export const getSuperLiveMessages = (since) =>
  api.get('/api/super/live-messages', { params: since ? { since } : {} });
export const getSuperTotalMessages = () => api.get('/api/super/total-messages');
export const getSystemClock = () => api.get('/api/super/system-clock');

// Users (Admin)
export const getUsers = () => api.get('/api/users');
export const createUser = (data) => api.post('/api/users', data);
export const updateUser = (id, data) => api.patch(`/api/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/api/users/${id}`);

// WhatsApp Connection
export const getDevices = () => api.get('/api/wzap/devices');
export const getQRCode = (phone) => api.get('/api/wzap/qr', { params: phone ? { phone } : {} });
export const getSession = () => api.get('/api/wzap/session');
export const getDeviceHealth = () => api.get('/api/wzap/health');
export const getProviderInfo = () => api.get('/api/wzap/provider');
export const getWhatsAppDisclaimer = () => api.get('/api/wzap/disclaimer');
export const ackWhatsAppDisclaimer = () => api.post('/api/wzap/disclaimer/ack');
export const getWzapNotifySettings = () => api.get('/api/wzap/notify-settings');
export const setWzapNotifySettings = (payload) => api.post('/api/wzap/notify-settings', payload);
export const getNumberHealth = () => api.get('/api/number-health');
export const getSuperNumberHealth = () => api.get('/api/super/number-health');

// Contacts
export const getContacts = (params) => api.get('/api/contacts', { params });
export const createContact = (data) => api.post('/api/contacts', data);
export const updateContact = (id, data) => api.patch(`/api/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/api/contacts/${id}`);
export const bulkDeleteContacts = (payload) => api.post('/api/contacts/bulk-delete', payload);
export const bulkUpdateContactGroups = (payload) => api.post('/api/contacts/bulk-groups', payload);
export const createGroup = (name) => api.post('/api/groups', { name });
export const importContacts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/contacts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getContactTags = () => api.get('/api/contacts/tags');

// Messages
export const sendMessage = (data) => api.post('/api/wzap/send', data);
export const getActiveSendJob = () => api.get('/api/send-jobs/active');
export const getSendJobProgress = (jobId) => api.get(`/api/send-jobs/${jobId}/progress`);
export const checkNumberExists = (phone) => api.post('/api/wzap/exists', { phone });
export const getMessages = (params) => api.get('/api/messages', { params });
export const getCampaignGroups = (params) => api.get('/api/messages/campaign-groups', { params });
export const getMessagesTimeline = (params) => api.get('/api/messages/timeline', { params });
export const uploadMessageAttachment = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/messages/attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getMessageAttachment = async (id) => {
  const response = await api.get(`/api/messages/${id}/attachment`, {
    responseType: 'blob'
  });
  return response;
};
export const refreshMessages = (onlyPending = false) => api.get('/api/messages/refresh', { params: { only_pending: onlyPending } });
export const getMessageCounter = () => api.get('/api/message-counter');

// Campaigns
export const getCampaigns = (params) => api.get('/api/campaigns', { params });
export const createCampaign = (data) => api.post('/api/campaigns', data);
export const updateCampaign = (id, data) => api.patch(`/api/campaigns/${id}`, data);
export const startCampaign = (id) => api.post(`/api/campaigns/${id}/start`);
export const retryCampaignFailed = (id) => api.post(`/api/campaigns/${id}/retry-failed`);
export const deleteCampaign = (id) => api.delete(`/api/campaigns/${id}`);
export const getCampaignProgress = (id) => api.get(`/api/campaigns/${id}/progress`);
export const refreshCampaignStatus = (id) => api.post(`/api/campaigns/${id}/refresh-status`);
export const scheduleCampaign = (id, scheduledAt) => api.post(`/api/campaigns/${id}/schedule`, null, { params: { scheduled_at: scheduledAt } });
export const cancelCampaignSchedule = (id) => api.post(`/api/campaigns/${id}/cancel-schedule`);
export const makeCampaignRecurring = (id, data) => api.post(`/api/campaigns/${id}/make-recurring`, data);
export const updateCampaignRecurrence = (id, data) => api.patch(`/api/campaigns/${id}/recurrence`, data);
export const toggleCampaignRecurrence = (id) => api.post(`/api/campaigns/${id}/recurrence/toggle`);
export const cancelCampaignRecurrence = (id) => api.post(`/api/campaigns/${id}/cancel-recurrence`);
export const getCampaignInstances = (id) => api.get(`/api/campaigns/${id}/instances`);
export const uploadCampaignAttachment = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/campaigns/${id}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getCampaignAttachment = async (id) => {
  const response = await api.get(`/api/campaigns/${id}/attachment`, {
    responseType: 'blob'
  });
  return response;
};
export const deleteCampaignAttachment = (id) => api.delete(`/api/campaigns/${id}/attachment`);

// Dashboard
export const getDashboard = () => api.get('/api/dashboard');

// Message Limits
export const getMessageLimits = () => api.get('/api/message-counter');

export default api;
